import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useUser } from '../../hooks/useApi';
import UserProfileDropdown from '../UserProfileDropdown';
import { CreditBalance } from '../CreditBalance';
import { menuNavItems, createNavItems, supportNavItem } from './navConfig';
import { getPageTitle } from '../../utils/routeTitles';
import { apiClient } from '../../services/api';
import LoadingOverlay from '../LoadingOverlay';

export default function TopBar() {
  const { isAuthenticated, user } = useAuth();
  const { user: userData } = useUser();
  const { openAuthModal } = useAuthModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  
  // Detect if we're on desktop (lg breakpoint: 1024px)
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    // Check on mount
    checkDesktop();
    
    // Check on resize
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);
  
  // Check if user has an active or canceling subscription
  const hasActiveSubscription = userData?.subscription_status === 'active' || userData?.subscription_status === 'canceling';
  
  // Open Stripe Customer Portal (no email login needed!)
  const openStripePortal = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false); // Close mobile menu
    
    if (!user?.id) {
      console.error('❌ [TopBar] No user ID available');
      alert('Unable to open billing portal. Please try refreshing the page.');
      return;
    }

    if (isLoadingPortal) {
      return; // Prevent multiple clicks
    }

    try {
      setIsLoadingPortal(true);
      
      // Create portal session via API
      const { url } = await apiClient.createPortalSession(
        user.id,
        `${window.location.origin}/my-collection`
      );
      
      // Redirect to Stripe portal (no email needed!)
      window.location.href = url;
    } catch (error) {
      console.error('❌ [TopBar] Failed to open billing portal:', error);
      
      // Close overlay first
      setIsLoadingPortal(false);
      
      // Show error after a brief delay to ensure overlay closes
      setTimeout(() => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.';
        alert(`${errorMessage}\n\nNote: If you're on dev environment, make sure you have a test subscription created with dev's test Stripe keys.`);
      }, 100);
    }
  };

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Handle body scroll lock and backdrop clicks
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsMenuOpen(false);
    }
  };

  const MobileNavLink = ({ to, label, icon, isExternal, openInNewTab, onClick }: { to: string; label: string; icon?: React.ReactNode; isExternal?: boolean; openInNewTab?: boolean; onClick?: (e: React.MouseEvent) => void }) => {
    const isActive = !isExternal && !onClick && (location.pathname === to || location.pathname.startsWith(to + '/'));
    
    const baseClasses = "flex items-center gap-4 px-6 min-h-[56px] transition-all duration-200";
    const activeClasses = isActive
      ? 'text-white bg-white/10 rounded-2xl'
      : 'text-white hover:bg-white/6 rounded-2xl';
    
    if (isExternal || onClick) {
      return (
        <a
          href={onClick ? '#' : to}
          onClick={onClick || (() => setIsMenuOpen(false))}
          className={`${baseClasses} ${activeClasses}`}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
          {icon && <span className="flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center">{icon}</span>}
          <span className="text-lg" style={{ fontSize: '18px', fontWeight: 400 }}>{label}</span>
        </a>
      );
    }
    
    return (
      <Link
        to={to}
        onClick={() => setIsMenuOpen(false)}
        className={`${baseClasses} ${activeClasses}`}
      >
        {icon && <span className="flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center">{icon}</span>}
        <span className="text-lg" style={{ fontSize: '18px', fontWeight: 400 }}>{label}</span>
      </Link>
    );
  };

  // Get Started button component - always visible, responsive sizing
  const GetStartedButton = () => (
    <button
      onClick={() => openAuthModal({ reason: 'get-started', fromPath: location.pathname })}
      className="bg-[#F9F7EA] text-[#0f1218] rounded-full font-semibold hover:bg-[#E7D7B1] transition-colors flex-shrink-0 whitespace-nowrap"
      style={{
        fontSize: 'clamp(12px, 3.5vw, 16px)',
        padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 3vw, 18px)',
        minWidth: 'clamp(90px, 25vw, 120px)',
        minHeight: 'clamp(36px, 10vw, 44px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      Get Started
    </button>
  );

  return (
    <>
      {/* Mobile layout */}
      <div className="lg:hidden bg-bg border-b border-white/10 px-2 sm:px-4 py-2 sm:py-3 relative z-50">
        <div className="flex items-center justify-between w-full gap-2">
          {/* Left: Hamburger menu */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="bg-black/50 border border-white/20 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors flex-shrink-0"
            aria-label="Toggle menu"
            style={{ 
              width: 'clamp(36px, 10vw, 40px)', 
              height: 'clamp(36px, 10vw, 40px)',
              minWidth: '36px', 
              minHeight: '36px' 
            }}
          >
            <svg
              className="text-white flex-shrink-0"
              style={{ width: 'clamp(18px, 5vw, 20px)', height: 'clamp(18px, 5vw, 20px)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          
          {/* Center/Left: Sparkle icon + Page Title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <svg
              className="w-4 h-4 text-white flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
              />
            </svg>
            <span className="text-white text-base truncate" style={{ fontSize: 'clamp(14px, 4vw, 18px)', fontWeight: 400 }}>
              {getPageTitle(location.pathname)}
            </span>
          </div>
          
          {/* Right: Auth buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {user && (
                  <>
                    <CreditBalance userId={user.id} />
                    <UserProfileDropdown user={user} />
                  </>
                )}
              </>
            ) : (
              <GetStartedButton />
            )}
          </div>
        </div>
      </div>

      {/* Desktop layout - top bar with page title */}
      <div className="hidden lg:flex bg-bg border-b border-white/10 py-3 fixed top-0 left-[280px] right-0 z-50 pl-4 pr-4">
        <div className="flex items-center justify-between w-full">
          {/* Left: Sparkle icon + Page Title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <svg
              className="w-4 h-4 text-white flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
              />
            </svg>
            <span className="text-white text-base truncate" style={{ fontWeight: 400 }}>
              {getPageTitle(location.pathname)}
            </span>
          </div>
          
          {/* Right side: Auth/CTA - Always visible, never hidden */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {user && (
                  <>
                    <CreditBalance userId={user.id} />
                    <UserProfileDropdown user={user} />
                  </>
                )}
              </>
            ) : (
              <GetStartedButton />
            )}
          </div>
        </div>
      </div>

      {/* Backdrop - Only render on mobile */}
      {!isDesktop && (
        <div
          onClick={handleBackdropClick}
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-200 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />
      )}

      {/* Mobile Drawer - Only render on mobile */}
      {!isDesktop && (
        <div
          ref={drawerRef}
          className={`fixed top-0 left-0 h-screen w-screen bg-bg z-50 lg:hidden flex flex-col transition-all duration-200 ease-out ${
            isMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full pointer-events-none'
          }`}
        >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2" onClick={() => setIsMenuOpen(false)}>
            <img src="/logo-icon.png" alt="GenAnything" className="w-10 h-10" />
            <span className="text-white text-xl" style={{ fontWeight: 400 }}>GenAnything</span>
          </Link>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="w-11 h-11 flex items-center justify-center transition-colors"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Drawer Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <nav className="flex flex-col">
            {/* Main navigation items */}
            <div className="flex flex-col py-2">
              {menuNavItems.map((item) => {
                // If this is the upgrade link and user has active subscription, open portal
                if (item.to === '/upgrade' && hasActiveSubscription) {
                  return (
                    <MobileNavLink 
                      key={item.to} 
                      to="#" 
                      label={isLoadingPortal ? "Loading..." : "Manage Subscription"}
                      icon={item.icon}
                      onClick={openStripePortal}
                    />
                  );
                }
                return (
                  <MobileNavLink 
                    key={item.to} 
                    to={item.to} 
                    label={item.label}
                    icon={item.icon}
                    isExternal={false}
                  />
                );
              })}
            </div>
            
            {/* Divider */}
            <div className="h-px bg-white/8 mx-6 my-4" />
            
            {/* Spacing for Create items */}
            <div className="mt-4 mb-3" />
            
            {/* Create navigation items */}
            <div className="flex flex-col py-2">
              {createNavItems.map((item) => (
                <MobileNavLink 
                  key={item.to} 
                  to={item.to} 
                  label={item.label}
                  icon={item.icon}
                  isExternal={false}
                />
              ))}
            </div>
            
            {/* Divider */}
            <div className="h-px bg-white/8 mx-6 my-4" />
            
            {/* Spacing for Support link */}
            <div className="mt-4 mb-3" />
            
            {/* Support link */}
            <div className="flex flex-col py-2">
              <MobileNavLink 
                to={supportNavItem.to}
                label={supportNavItem.label}
                icon={supportNavItem.icon}
                isExternal={true}
                openInNewTab={false}
              />
            </div>
          </nav>
        </div>

        {/* Footer - Empty container for spacing (no icons for now) */}
        <div className="px-6 pb-6 flex-shrink-0">
          {/* Footer area reserved for future icons if needed */}
        </div>
      </div>
      )}

      {/* Loading overlay for portal session */}
      <LoadingOverlay
        show={isLoadingPortal}
        message="Opening Billing Portal..."
      />
    </>
  );
}

